import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { AppProvider, useApp, IS_DEMO } from './context/AppContext';
import { MawLogoLogin } from './components/shared/MawLogo';
import Sidebar from './components/Layout/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import Transactions from './components/Transactions/Transactions';
import CalendarView from './components/Calendar/CalendarView';
import Accounts from './components/Accounts/Accounts';
import Goals from './components/Goals/Goals';
import Reports from './components/Reports/Reports';
import Investments from './components/Investments/Investments';
import SalaryDivision from './components/SalaryDivision/SalaryDivision';
import Forecasts from './components/Forecasts/Forecasts';
import Installments from './components/Installments/Installments';
import Subscriptions from './components/Subscriptions/Subscriptions';
import Categories from './components/Categories/Categories';
import Budgets from './components/Budgets/Budgets';
import Debts from './components/Debts/Debts';
import Login from './components/Auth/Login';
import { validateKey, saveLicense, loadLicense, PLAN_LABEL, hasFeature, getTrialStatus } from './utils/license';
import { auth } from './firebase';
import { onAuthStateChanged, signOut, updatePassword, updateProfile, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { loadCloudData, saveCloudData, deleteCloudData, backupToLocalStorage, savePreSyncBackup, getBackupInfo, CLOUD_NOT_FOUND, CLOUD_ERROR } from './utils/cloudSync';
import Help from './components/Help/Help';
import CardReport from './components/Cards/CardReport';
import AIConsultor from './components/AIConsultor/AIConsultor';
import Vehicles from './components/Vehicles/Vehicles';
import GettingStarted from './components/GettingStarted/GettingStarted';
import TaxReport from './components/TaxReport/TaxReport';
import MonthReport from './components/MonthReport/MonthReport';
import Plans from './components/Plans/Plans';
import TransactionModal from './components/Transactions/TransactionModal';
import TransferModal from './components/Transfers/TransferModal';
import { NAV_GROUPS, BOTTOM_NAV } from './navigation';
import Onboarding from './components/Onboarding/Onboarding';
import ToastContainer from './components/shared/Toast';
import { toast } from './components/shared/Toast';
import { isPushSupported, requestPushPermission, disablePushNotifications, refreshPushToken } from './utils/pushNotifications';
import { WhatsNewBanner, NovidadesModal } from './components/WhatsNew/Novidades';
import { calcCardDueDate, fmtDateBR } from './utils/cards';

function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState(0); // 0=hidden 1=visible 2=exit

  useEffect(() => {
    const raf = requestAnimationFrame(() => setPhase(1));
    const t1 = setTimeout(() => setPhase(2), 2000);
    const t2 = setTimeout(() => onDone(), 2700);
    return () => { cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); };
  }, []); // eslint-disable-line

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#0d1b2a',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 0,
      opacity: phase === 2 ? 0 : phase === 0 ? 0 : 1,
      transition: phase === 2 ? 'opacity 0.7s ease' : 'opacity 0.4s ease',
      pointerEvents: phase === 2 ? 'none' : 'all',
    }}>
      {/* Logo */}
      <div style={{
        transform: phase >= 1 ? 'scale(1) translateY(0)' : 'scale(0.82) translateY(24px)',
        opacity: phase >= 1 ? 1 : 0,
        transition: 'transform 0.65s cubic-bezier(0.16,1,0.3,1), opacity 0.5s ease',
      }}>
        <MawLogoLogin />
      </div>

      {/* Loading bar */}
      <div style={{ width: 200, height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 99, marginTop: 48, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 99,
          background: 'linear-gradient(90deg, #c9a227, #f0d060)',
          width: phase >= 2 ? '100%' : phase >= 1 ? '78%' : '0%',
          transition: phase >= 2 ? 'width 0.5s ease' : phase >= 1 ? 'width 1.9s ease' : 'none',
        }} />
      </div>

      {/* Label */}
      <div style={{
        marginTop: 18, fontSize: 11, letterSpacing: 3,
        color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase',
        opacity: phase >= 1 ? 1 : 0, transition: 'opacity 0.6s ease 0.3s',
      }}>
        Carregando
      </div>
    </div>
  );
}

const SHORTCUT_LABELS = [
  { key: 'D', desc: 'Dashboard' }, { key: 'T', desc: 'Lançamentos' },
  { key: 'A', desc: 'Contas' },    { key: 'I', desc: 'Investimentos' },
  { key: 'M', desc: 'Metas' },     { key: 'O', desc: 'Orçamento' },
  { key: '?', desc: 'Atalhos' },   { key: 'Esc', desc: 'Fechar modal' },
];

function ShortcutsModal({ onClose }) {
  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 380 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">Atalhos de teclado <button className="close-btn" onClick={onClose}>×</button></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
          {SHORTCUT_LABELS.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface2)', borderRadius: 8 }}>
              <kbd style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700, color: 'var(--text)', boxShadow: '0 2px 0 var(--border)', minWidth: 32, textAlign: 'center' }}>{s.key}</kbd>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{s.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileTopbar({ onMenuOpen }) {
  const { state, dispatch, unreadNotifs } = useApp();
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [novidadesOpen, setNovidadesOpen] = React.useState(false);
  const notifStyle = {
    warning: { icon: 'fa-triangle-exclamation', color: '#f97316' },
    info:    { icon: 'fa-circle-info',           color: '#378add' },
    success: { icon: 'fa-check-circle',          color: '#1d9e75' },
    error:   { icon: 'fa-circle-exclamation',    color: '#e24b4a' },
  };

  return (
    <>
      <div className="mobile-topbar">
        {/* Hamburguer */}
        <button
          onClick={onMenuOpen}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: 20, padding: '8px 4px', display: 'flex', alignItems: 'center' }}
        >
          <i className="fas fa-bars"></i>
        </button>

        {/* Logo / título da página */}
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gold)', letterSpacing: 0.5 }}>
          MAW Finanças
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Olho — modo privacidade */}
        <button
          onClick={() => dispatch({ type: 'TOGGLE_PRIVACY' })}
          title={state.privacyMode ? 'Mostrar valores' : 'Ocultar valores'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: state.privacyMode ? 'var(--gold)' : 'rgba(255,255,255,0.7)', fontSize: 17, padding: '8px 4px', display: 'flex', alignItems: 'center' }}
        >
          <i className={`fas ${state.privacyMode ? 'fa-eye-slash' : 'fa-eye'}`}></i>
        </button>

        {/* Novidades */}
        <button
          onClick={() => setNovidadesOpen(true)}
          title="Novidades"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: 16, padding: '8px 4px', display: 'flex', alignItems: 'center' }}
        >
          <i className="fas fa-sparkles"></i>
        </button>

        {/* Bell */}
        <button
          onClick={() => setNotifOpen(o => !o)}
          style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: 18, padding: '8px 4px', display: 'flex', alignItems: 'center' }}
        >
          <i className="fas fa-bell"></i>
          {unreadNotifs > 0 && (
            <span style={{ position: 'absolute', top: 4, right: 2, minWidth: 16, height: 16, borderRadius: 99, background: '#e24b4a', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
              {unreadNotifs > 9 ? '9+' : unreadNotifs}
            </span>
          )}
        </button>
        </div>
      </div>

      {/* Notification panel (mobile) */}
      {notifOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setNotifOpen(false)} />
          <div style={{ position: 'fixed', top: 56, left: 0, right: 0, bottom: 0, zIndex: 200, background: 'var(--card-bg)', overflowY: 'auto', borderTop: '1px solid var(--border)' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Notificações</span>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {unreadNotifs > 0 && (
                  <button onClick={() => dispatch({ type: 'CLEAR_NOTIFICATIONS' })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--blue)', fontWeight: 500 }}>
                    Marcar lidas
                  </button>
                )}
                <button onClick={() => setNotifOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--muted)', lineHeight: 1 }}>×</button>
              </div>
            </div>
            {state.notifications.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10, color: 'var(--muted)' }}>
                <i className="fas fa-bell-slash" style={{ fontSize: 32, opacity: 0.3 }}></i>
                <div style={{ fontSize: 13 }}>Nenhuma notificação</div>
              </div>
            ) : state.notifications.map(n => {
              const s = notifStyle[n.type] || { icon: 'fa-bell', color: 'var(--muted)' };
              return (
                <div key={n.id} onClick={() => dispatch({ type: 'MARK_NOTIF_READ', payload: n.id })}
                  style={{ display: 'flex', gap: 12, padding: '12px 16px', background: n.read ? 'transparent' : `${s.color}0d`, borderLeft: `3px solid ${n.read ? 'transparent' : s.color}`, cursor: 'pointer' }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`fas ${s.icon}`} style={{ color: s.color, fontSize: 13 }}></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: n.read ? 500 : 700, fontSize: 13, color: 'var(--text)' }}>{n.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{n.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {novidadesOpen && <NovidadesModal onClose={() => setNovidadesOpen(false)} />}
    </>
  );
}

function IOSInstallSheet({ onDismiss }) {
  const ua = navigator.userAgent;
  const isChromeiOS = /CriOS/i.test(ua);
  const isFxiOS = /FxiOS/i.test(ua);
  const isOtherBrowser = isChromeiOS || isFxiOS;
  const browserName = isChromeiOS ? 'Chrome' : isFxiOS ? 'Firefox' : 'outro navegador';

  const Step = ({ n, icon, text, note }) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{n}</div>
      <div style={{ paddingTop: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 7 }}>
          <i className={`fas ${icon}`} style={{ color: 'var(--gold)', fontSize: 13, width: 16 }}></i>
          {text}
        </div>
        {note && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{note}</div>}
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9990, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      {/* backdrop */}
      <div onClick={onDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />

      {/* sheet */}
      <div style={{
        position: 'relative',
        background: 'var(--card-bg)',
        borderRadius: '22px 22px 0 0',
        padding: '8px 22px',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.25)',
        animation: 'slideInSheet 0.32s cubic-bezier(0.16,1,0.3,1)',
      }}>
        {/* handle */}
        <div style={{ width: 40, height: 4, background: 'var(--border)', borderRadius: 99, margin: '10px auto 20px' }} />

        {/* close */}
        <button onClick={onDismiss} style={{ position: 'absolute', top: 16, right: 18, background: 'var(--surface2)', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', color: 'var(--muted)', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>

        {/* header */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 22 }}>
          <img src="./icons/icon-192.png" alt="MAW" style={{ width: 54, height: 54, borderRadius: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.18)' }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)' }}>Instalar MAW Finanças</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Adicione à tela de início como um app</div>
          </div>
        </div>

        {isOtherBrowser ? (
          <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 6 }}>
              <i className="fas fa-triangle-exclamation" style={{ marginRight: 6 }}></i>
              Abra no Safari para instalar
            </div>
            <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.5 }}>
              Você está usando o <strong>{browserName}</strong>. Para instalar o MAW Finanças como app, copie o link e abra no <strong>Safari</strong>.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 24 }}>
            <Step n={1} icon="fa-arrow-up-from-bracket" text='Toque no botão Compartilhar' note='Ícone de seta na barra inferior do Safari' />
            <Step n={2} icon="fa-square-plus" text='"Adicionar à Tela de Início"' note='Role para baixo no menu de compartilhar' />
            <Step n={3} icon="fa-check" text='Toque em Adicionar' note='O app vai aparecer na sua tela de início!' />
          </div>
        )}

        {/* animated arrow pointing down */}
        {!isOtherBrowser && (
          <div style={{ textAlign: 'center', paddingBottom: 4 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>O botão de compartilhar fica aqui embaixo</div>
            <i className="fas fa-chevron-down" style={{ color: 'var(--gold)', fontSize: 18, animation: 'iosBounce 1.2s ease-in-out infinite' }}></i>
          </div>
        )}
      </div>
    </div>
  );
}

function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem('maw_pwa_dismissed'));

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone) return; // iOS standalone check
    const ua = navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    if (ios) { setIsIOS(true); return; }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (dismissed) return null;
  if (window.matchMedia('(display-mode: standalone)').matches) return null;
  if (window.navigator.standalone) return null;
  if (!deferredPrompt && !isIOS) return null;

  const dismiss = () => {
    localStorage.setItem('maw_pwa_dismissed', '1');
    setDismissed(true);
    setShowSheet(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  };

  return (
    <>
      {showSheet && <IOSInstallSheet onDismiss={() => setShowSheet(false)} />}

      <div className="pwa-banner" onClick={isIOS ? () => setShowSheet(true) : undefined} style={isIOS ? { cursor: 'pointer' } : {}}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-mobile-screen" style={{ color: 'var(--gold)', fontSize: 17 }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>Instalar MAW Finanças</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
              {isIOS
                ? <><i className="fas fa-arrow-up-from-bracket" style={{ marginRight: 4, color: 'var(--gold)' }}></i>Toque aqui para ver como instalar no iPhone</>
                : 'Instale o app para acesso rápido, sem abrir o navegador'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {!isIOS && (
            <button className="btn btn-primary btn-sm" onClick={install} style={{ fontSize: 12 }}>
              <i className="fas fa-download"></i> Instalar
            </button>
          )}
          {isIOS && (
            <button onClick={() => setShowSheet(true)} style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Ver como
            </button>
          )}
          <button onClick={dismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
      </div>
    </>
  );
}

function SidebarDrawer({ open, onClose }) {
  const { state, dispatch } = useApp();
  const PL = PLAN_LABEL;
  const [lockedAlert, setLockedAlert] = React.useState(null);

  if (!open) return null;

  const nav = NAV_GROUPS;

  const plan = state.license?.plan;
  const trial = getTrialStatus(state.user, state.license);

  const go = (id) => {
    if (!hasFeature(plan, id, trial.active)) { setLockedAlert(id); return; }
    dispatch({ type: 'SET_TAB', payload: id });
    onClose();
  };

  const initials = (state.user?.name || 'U')
    .split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <div className="sidebar-drawer-overlay" onClick={onClose} />
      <div className="sidebar-drawer">
        {/* Logo */}
        <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--gold)', fontWeight: 800, fontSize: 17, letterSpacing: 0.5 }}>MAW Finanças</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 20 }}>×</button>
        </div>

        {/* User strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', overflow: 'hidden', background: 'linear-gradient(135deg,var(--navy),var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {state.user?.avatar
              ? <img src={state.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{initials}</span>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state.user?.name || 'Usuário'}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Minha conta</div>
          </div>
          <button className="btn btn-danger btn-sm" onClick={() => { signOut(auth); onClose(); }} style={{ fontSize: 11, padding: '5px 10px' }}>
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {nav.map(section => (
            <div key={section.group} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, padding: '8px 16px 4px', textTransform: 'uppercase' }}>{section.group}</div>
              {section.items.map(item => {
                const locked = !hasFeature(plan, item.id, trial.active);
                return (
                  <button
                    key={item.id}
                    onClick={() => go(item.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px', background: state.activeTab === item.id ? 'rgba(201,162,39,0.15)' : 'none',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      color: state.activeTab === item.id ? 'var(--gold)' : locked ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.75)',
                      fontSize: 14, fontWeight: state.activeTab === item.id ? 600 : 400,
                      borderLeft: state.activeTab === item.id ? '3px solid var(--gold)' : '3px solid transparent',
                      transition: 'all 0.12s',
                    }}
                  >
                    <i className={`fas ${item.icon}`} style={{ fontSize: 15, width: 18, textAlign: 'center' }}></i>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {locked && <i className="fas fa-lock" style={{ fontSize: 10, opacity: 0.4 }}></i>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom */}
        <div style={{ padding: '8px 12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {plan ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(201,162,39,0.12)', borderRadius: 8, padding: '6px 10px', border: '1px solid rgba(201,162,39,0.25)', marginBottom: 8 }}>
              <i className="fas fa-medal" style={{ fontSize: 11, color: '#c9a227' }}></i>
              <span style={{ fontSize: 11, color: '#c9a227', fontWeight: 600 }}>Plano {PL[plan]}</span>
            </div>
          ) : trial.active ? (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                background: trial.daysLeft <= 2 ? 'rgba(226,75,74,0.12)' : 'rgba(29,158,117,0.12)',
                borderRadius: 8, padding: '7px 10px',
                border: `1px solid ${trial.daysLeft <= 2 ? 'rgba(226,75,74,0.3)' : 'rgba(29,158,117,0.3)'}`,
              }}>
                <i className="fas fa-clock" style={{ fontSize: 11, color: trial.daysLeft <= 2 ? '#e24b4a' : '#1d9e75' }}></i>
                <span style={{ fontSize: 11, color: trial.daysLeft <= 2 ? '#e24b4a' : '#1d9e75', fontWeight: 600 }}>
                  {trial.daysLeft === 1 ? 'Último dia de teste!' : `${trial.daysLeft} dias de teste`}
                </span>
              </div>
              {trial.daysLeft <= 2 && (
                <a
                  href={PLANS_URL}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'block', marginBottom: 8, padding: '8px 12px',
                    background: '#e24b4a', color: '#fff',
                    borderRadius: 8, fontSize: 12, fontWeight: 700,
                    textAlign: 'center', textDecoration: 'none',
                  }}
                >
                  <i className="fas fa-crown" style={{ marginRight: 6 }}></i>
                  Ver planos e preços
                </a>
              )}
            </>
          ) : null}
        </div>

        {lockedAlert && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setLockedAlert(null)}>
            <div style={{ background: '#132236', border: '1px solid #1e3a5f', borderRadius: 16, padding: '28px 24px', maxWidth: 320, width: '100%', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔒</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', marginBottom: 8 }}>Recurso bloqueado</div>
              <div style={{ color: '#a0b4c8', fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>
                Faça upgrade para o plano <strong style={{ color: '#c9a227' }}>Pro</strong> ou <strong style={{ color: '#c9a227' }}>Família</strong> para desbloquear.
              </div>
              <button onClick={() => setLockedAlert(null)} style={{ width: '100%', padding: '10px', background: '#c9a227', color: '#0d1b2a', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Entendido</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const PLANS_URL = 'https://www.financasmaw.com.br/plano.html';

function TrialExpiredScreen() {
  const { dispatch } = useApp();
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const activate = () => {
    setError('');
    const plan = validateKey(key);
    if (!plan) { setError('Chave inválida. Verifique e tente novamente.'); return; }
    setLoading(true);
    setTimeout(() => {
      saveLicense(key.trim().toUpperCase(), plan);
      dispatch({ type: 'SET_LICENSE', payload: { key: key.trim().toUpperCase(), plan, activatedAt: new Date().toISOString(), email: auth.currentUser?.email ?? null } });
    }, 700);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#0d1b2a',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24, overflowY: 'auto',
    }}>
      <MawLogoLogin />
      <div style={{ marginTop: 28, textAlign: 'center', maxWidth: 420, width: '100%' }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>⏰</div>
        <div style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
          Seu período de teste encerrou
        </div>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, lineHeight: 1.65, marginBottom: 28 }}>
          Os 7 dias de teste gratuito chegaram ao fim.<br />
          Adquira sua licença para continuar usando o MAW Finanças.
        </p>

        {/* Activate existing key */}
        <div style={{
          background: 'rgba(255,255,255,0.05)', borderRadius: 14,
          padding: '20px 20px 16px', marginBottom: 16,
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, marginBottom: 10, textAlign: 'left' }}>
            <i className="fas fa-key" style={{ color: '#c9a227', marginRight: 7 }}></i>
            Já tem uma chave de licença?
          </div>
          <input
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: `1px solid ${error ? '#f87171' : 'rgba(255,255,255,0.2)'}`,
              background: 'rgba(255,255,255,0.08)', color: '#fff',
              fontSize: 14, fontFamily: 'monospace', letterSpacing: 1,
              boxSizing: 'border-box', outline: 'none',
            }}
            placeholder="MAW-X-XXXXXX-XXXXXXXX"
            value={key}
            onChange={e => { setKey(e.target.value.toUpperCase()); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && activate()}
          />
          {error && (
            <div style={{ color: '#f87171', fontSize: 12, marginTop: 5, textAlign: 'left' }}>
              <i className="fas fa-circle-exclamation" style={{ marginRight: 4 }}></i>{error}
            </div>
          )}
          <button
            onClick={activate}
            disabled={loading || !key.trim()}
            style={{
              width: '100%', marginTop: 10, padding: '11px',
              background: loading || !key.trim() ? 'rgba(201,162,39,0.4)' : '#c9a227',
              border: 'none', borderRadius: 8,
              color: '#0d1b2a', fontWeight: 700, fontSize: 14, cursor: loading || !key.trim() ? 'default' : 'pointer',
            }}
          >
            {loading
              ? <><i className="fas fa-circle-notch fa-spin" style={{ marginRight: 6 }}></i>Ativando...</>
              : <><i className="fas fa-unlock" style={{ marginRight: 6 }}></i>Ativar licença</>}
          </button>
        </div>

        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginBottom: 12 }}>— ou —</div>

        {/* Ver planos */}
        <a
          href={PLANS_URL}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: '100%', padding: '13px', background: '#c9a227',
            border: 'none', borderRadius: 10, color: '#0d1b2a',
            fontWeight: 700, fontSize: 14, textDecoration: 'none',
            boxSizing: 'border-box',
          }}
        >
          <i className="fas fa-medal" style={{ fontSize: 16 }}></i>
          Ver planos e preços
        </a>

        <button
          onClick={() => { signOut(auth); }}
          style={{
            marginTop: 20, background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.3)', fontSize: 12, cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Sair da conta
        </button>
      </div>
    </div>
  );
}

function DemoBanner() {
  return (
    <div style={{
      background: 'linear-gradient(90deg, #0d2d4a 0%, #1a3a5c 100%)',
      borderBottom: '2px solid #c9a227',
      padding: '9px 18px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexWrap: 'wrap', fontSize: 13, flexShrink: 0, zIndex: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.9)' }}>
        <i className="fas fa-flask" style={{ color: '#c9a227', fontSize: 15 }}></i>
        <span>
          <strong style={{ color: '#c9a227' }}>Modo Demonstração</strong>
          {' '}— Dados fictícios para explorar o sistema. Nada é salvo.
        </span>
      </div>
      <button
        onClick={() => { window.location.href = window.location.pathname; }}
        style={{
          background: '#c9a227', border: 'none', borderRadius: 8,
          color: '#0d1b2a', fontWeight: 700, fontSize: 12,
          padding: '7px 16px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
        }}
      >
        <i className="fas fa-user-plus" style={{ marginRight: 6 }}></i>Criar minha conta
      </button>
    </div>
  );
}

function UpdateBanner() {
  const [reg, setReg] = useState(null);
  useEffect(() => {
    const handler = (e) => setReg(e.detail);
    window.addEventListener('maw-sw-update', handler);
    return () => window.removeEventListener('maw-sw-update', handler);
  }, []);
  if (!reg) return null;
  const doUpdate = () => {
    if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  };
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9998, background: '#0d1b2a', border: '1px solid #c9a227',
      borderRadius: 14, padding: '14px 18px',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 4px 32px rgba(0,0,0,0.35)',
      maxWidth: 420, width: 'calc(100% - 32px)',
    }}>
      <i className="fas fa-arrow-up-from-bracket" style={{ color: '#c9a227', fontSize: 18, flexShrink: 0 }}></i>
      <div style={{ flex: 1 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Nova versão disponível!</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 }}>Clique em atualizar para usar a versão mais recente.</div>
      </div>
      <button onClick={doUpdate} style={{
        background: '#c9a227', border: 'none', borderRadius: 8,
        color: '#0d1b2a', fontWeight: 700, fontSize: 12,
        padding: '8px 14px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
      }}>
        <i className="fas fa-rotate-right" style={{ marginRight: 5 }}></i>Atualizar
      </button>
    </div>
  );
}

function LicenseBanner() {
  const { dispatch } = useApp();
  return (
    <div style={{
      background: 'linear-gradient(90deg, #854F0B 0%, #c9a227 100%)',
      color: '#fff',
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <i className="fas fa-key" style={{ fontSize: 15 }}></i>
        <span><strong>Licença não ativada.</strong> Ative sua licença para garantir acesso contínuo ao sistema.</span>
      </div>
      <button
        onClick={() => dispatch({ type: 'SET_TAB', payload: 'profile' })}
        style={{
          background: 'rgba(255,255,255,0.2)',
          border: '1px solid rgba(255,255,255,0.5)',
          color: '#fff',
          borderRadius: 8,
          padding: '6px 16px',
          fontWeight: 700,
          fontSize: 12,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Ativar agora
      </button>
    </div>
  );
}

function AppContent() {
  const { state, dispatch } = useApp();
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showNovidades, setShowNovidades] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cloudLoading, setCloudLoading] = useState(!IS_DEMO);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const showOnboarding = state.user && state.accounts.length === 0 && !localStorage.getItem('maw_onboarding_done');

  // Firebase auth + Firestore sync
  useEffect(() => {
    if (IS_DEMO) return;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const profile = (() => { try { return JSON.parse(localStorage.getItem('maw_profile') || 'null'); } catch { return null; } })();
        const resolvedName = profile?.name || firebaseUser.displayName || firebaseUser.email.split('@')[0];
        const resolvedInitials = resolvedName.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();
        dispatch({
          type: 'SET_USER',
          payload: {
            email: firebaseUser.email,
            name: resolvedName,
            phone: profile?.phone || null,
            initials: profile?.initials || resolvedInitials,
            avatar: profile?.avatar || null,
            createdAt: firebaseUser.metadata.creationTime,
          },
        });

        // Backup pré-sincronização — salva estado local ANTES de qualquer operação na nuvem
        savePreSyncBackup(stateRef.current);

        // Carrega dados financeiros da nuvem
        const familyId = stateRef.current.familyId;
        const cloudData = await loadCloudData(firebaseUser.uid, familyId);

        if (cloudData === CLOUD_ERROR) {
          // Falha de rede / permissão — NÃO sobrescreve nada, usa dados locais
          console.warn('MAW: Firestore inacessível. Usando dados locais.');
          dispatch({ type: 'SET_CLOUD_LOADED' });
        } else if (cloudData === CLOUD_NOT_FOUND) {
          // Primeiro acesso real — sobe os dados locais para a nuvem
          const cur = stateRef.current;
          const hasLocal = (cur.accounts?.length > 0) || (cur.transactions?.length > 0);
          if (hasLocal) {
            await saveCloudData(firebaseUser.uid, cur, familyId).catch(() => {});
          }
          dispatch({ type: 'SET_CLOUD_LOADED' });
        } else {
          // Dados encontrados na nuvem — carrega e salva backup local
          backupToLocalStorage(cloudData);
          dispatch({ type: 'LOAD_CLOUD_DATA', payload: cloudData });
        }

        // Renova token FCM silenciosamente se push já estava ativado
        const prefs = (() => { try { return JSON.parse(localStorage.getItem('maw_prefs') || '{}'); } catch { return {}; } })();
        if (prefs.pushEnabled) refreshPushToken(firebaseUser.uid).catch(() => {});
      } else {
        dispatch({ type: 'LOGOUT' });
      }
      setCloudLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      const map = { d: 'dashboard', t: 'transactions', a: 'accounts', i: 'investments', m: 'goals', o: 'budgets' };
      if (map[k]) { dispatch({ type: 'SET_TAB', payload: map[k] }); return; }
      if (e.key === '?') { setShowShortcuts(s => !s); return; }
      if (e.key === 'Escape') setShowShortcuts(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch]);

  // Aguarda verificação do Firebase Auth
  if (cloudLoading) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'var(--bg)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
      }}>
        <MawLogoLogin />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, color: 'var(--muted)', fontSize: 13 }}>
          <i className="fas fa-circle-notch fa-spin" style={{ color: 'var(--gold)' }}></i>
          Sincronizando seus dados...
        </div>
      </div>
    );
  }

  if (!state.user) return <Login />;

  const trialStatus = getTrialStatus(state.user, state.license);
  if (!IS_DEMO && trialStatus.expired) return <TrialExpiredScreen />;

  const pages = {
    dashboard: <Dashboard />,
    transactions: <Transactions />,
    calendar: <CalendarView />,
    accounts: <Accounts />,
    goals: <Goals />,
    reports: <Reports />,
    investments: <Investments />,
    budgets: <Budgets />,
    debts: <Debts />,
    'card-report': <CardReport />,
    profile: <ProfilePage />,
    categories: <Categories />,
    vehicles: <Vehicles />,
    'tax-report': <TaxReport />,
    'month-report': <MonthReport />,
    'getting-started': <GettingStarted />,
    plans: <Plans />,
    help: <Help />,
    'ai-consultor': <AIConsultor />,
    'salary-division': <SalaryDivision />,
    forecasts: <Forecasts />,
    installments: <Installments />,
    subscriptions: <Subscriptions />,
  };

  const page = pages[state.activeTab] || pages.dashboard;
  const isDashboard = state.activeTab === 'dashboard';

  return (
    <div className="app-shell">
      <Sidebar />
      <SidebarDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="page-area">
        <MobileTopbar onMenuOpen={() => setDrawerOpen(true)} />
        {IS_DEMO && <DemoBanner />}
        {!IS_DEMO && !state.license && !trialStatus.active && <LicenseBanner />}
        <PWAInstallBanner />
        <div key={state.activeTab} className="page-enter">
          {isDashboard ? page : (
            <div className="page-content-inner">{page}</div>
          )}
        </div>
      </div>
      {state.activeTab !== 'plans' && <GlobalAddButton />}
      <MobileBottomNav />
      {!IS_DEMO && <WhatsNewBanner onOpenHistory={() => setShowNovidades(true)} />}
      <ToastContainer />
      {showOnboarding && <Onboarding onDone={() => dispatch({ type: 'SET_TAB', payload: 'dashboard' })} />}
      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
      {showNovidades && <NovidadesModal onClose={() => setShowNovidades(false)} />}
      <UpdateBanner />
    </div>
  );
}

// ── Botão Adicionar global (FAB) — disponível em qualquer tela ────────────────
function GlobalAddButton() {
  const { state } = useApp();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);

  // Esconde no Dashboard (que já tem os botões próprios no header)
  if (state.activeTab === 'dashboard') return null;

  const options = [
    { id: 'income',   label: 'Receita',       icon: 'fa-arrow-up',                 color: '#1d9e75' },
    { id: 'expense',  label: 'Despesa',       icon: 'fa-arrow-down',               color: '#e24b4a' },
    { id: 'transfer', label: 'Transferência', icon: 'fa-arrow-right-arrow-left',   color: '#378ADD' },
  ];

  return (
    <>
      {/* Menu de opções */}
      {open && (
        <>
          <div className="global-fab" style={{ position: 'fixed', inset: 0, zIndex: 690 }} onClick={() => setOpen(false)} />
          <div className="global-fab" style={{ position: 'fixed', right: 22, bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))', zIndex: 691, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            {options.map((o, i) => (
              <button
                key={o.id}
                onClick={() => { setModal(o.id); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'var(--card-bg)', border: '1px solid var(--border)',
                  borderRadius: 99, padding: '10px 16px', cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                  fontWeight: 600, fontSize: 13, color: 'var(--text)',
                  animation: `slideInLeft 0.${15 + i * 5}s ease`,
                }}
              >
                <span style={{ width: 26, height: 26, borderRadius: '50%', background: `${o.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={`fas ${o.icon}`} style={{ fontSize: 11, color: o.color }}></i>
                </span>
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* FAB */}
      <button
        className="global-fab"
        onClick={() => setOpen(o => !o)}
        title="Adicionar"
        style={{
          position: 'fixed', right: 22, bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
          zIndex: 692,
          width: 54, height: 54, borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--navy), #1e3a52)',
          border: '2px solid var(--gold)', cursor: 'pointer',
          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.2s ease',
          transform: open ? 'rotate(45deg)' : 'rotate(0)',
        }}
      >
        <i className="fas fa-plus" style={{ fontSize: 20, color: 'var(--gold)' }}></i>
      </button>

      {/* Modais */}
      {(modal === 'income' || modal === 'expense') && (
        <TransactionModal defaultType={modal} onClose={() => setModal(null)} />
      )}
      {modal === 'transfer' && <TransferModal onClose={() => setModal(null)} />}
    </>
  );
}

// ── Sheet "Nova transação" (mobile) — cards estilo guiado ──────────────────────
function AddSheet({ onPick, onClose }) {
  const cards = [
    { id: 'income',   label: 'Receita',       desc: 'Adicionar uma entrada de valores (salário, venda, reembolso...)', icon: 'fa-arrow-down-left-and-arrow-up-right-to-center', arrow: 'fa-arrow-down', color: '#1d9e75' },
    { id: 'expense',  label: 'Despesa',       desc: 'Adicionar uma saída de valores (compra, pagamento, conta...)',     icon: 'fa-arrow-up', arrow: 'fa-arrow-up',   color: '#e24b4a' },
    { id: 'transfer', label: 'Transferência', desc: 'Mover valores entre suas contas (não conta como receita/despesa)', icon: 'fa-arrow-right-arrow-left', arrow: 'fa-arrow-right-arrow-left', color: '#378ADD' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 700, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg)', width: '100%',
          borderRadius: '22px 22px 0 0', padding: '10px 16px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          animation: 'slideInSheet 0.28s cubic-bezier(0.16,1,0.3,1)',
          maxHeight: '80vh', overflowY: 'auto',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 99, background: 'var(--border)', margin: '4px auto 14px' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)' }}>Nova transação</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--muted)', lineHeight: 1 }}>×</button>
        </div>

        {cards.map(c => (
          <button
            key={c.id}
            onClick={() => onPick(c.id)}
            style={{
              width: '100%', textAlign: 'left', marginBottom: 10, cursor: 'pointer',
              background: 'var(--card-bg)', border: `1.5px solid ${c.color}55`,
              borderRadius: 16, padding: '14px 16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 16, color: c.color, marginBottom: 4 }}>
              <i className={`fas ${c.arrow}`} style={{ fontSize: 14 }}></i> {c.label}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>{c.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Barra de navegação inferior fixa (mobile) — estilo app nativo ──────────────
function MobileBottomNav() {
  const { state, dispatch } = useApp();
  const [sheet, setSheet] = useState(false);
  const [modal, setModal] = useState(null);

  const plan = state.license?.plan;
  const trial = getTrialStatus(state.user, state.license);

  const go = (id) => {
    if (!hasFeature(plan, id, trial.active)) {
      dispatch({ type: 'SET_TAB', payload: 'plans' });
      return;
    }
    dispatch({ type: 'SET_TAB', payload: id });
  };

  const Item = ({ item }) => {
    const active = state.activeTab === item.id;
    return (
      <button onClick={() => go(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '6px 2px' }}>
        <i className={`fas ${item.icon}`} style={{ fontSize: 18, color: active ? 'var(--gold)' : 'var(--muted)' }}></i>
        <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, color: active ? 'var(--gold)' : 'var(--muted)' }}>{item.label}</span>
      </button>
    );
  };

  return (
    <>
      <div className="mobile-bottom-nav">
        <Item item={BOTTOM_NAV[0]} />
        <Item item={BOTTOM_NAV[1]} />

        {/* Botão central de adicionar */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => setSheet(true)}
            aria-label="Nova transação"
            className="nav-fab-btn"
            style={{
              width: 52, height: 52, borderRadius: '50%', marginTop: -22,
              background: 'linear-gradient(135deg, var(--navy), #1e3a52)',
              border: '2.5px solid var(--gold)', cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(13,27,42,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className="fas fa-plus" style={{ fontSize: 20, color: 'var(--gold)' }}></i>
          </button>
        </div>

        <Item item={BOTTOM_NAV[2]} />
        <Item item={BOTTOM_NAV[3]} />
      </div>

      {sheet && <AddSheet onPick={(id) => { setSheet(false); setModal(id); }} onClose={() => setSheet(false)} />}

      {(modal === 'income' || modal === 'expense') && (
        <TransactionModal defaultType={modal} onClose={() => setModal(null)} />
      )}
      {modal === 'transfer' && <TransferModal onClose={() => setModal(null)} />}
    </>
  );
}

function ClearDataModal({ onClose, onConfirm }) {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setError('');
    if (!password) { setError('Digite sua senha para confirmar.'); return; }
    setLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      onConfirm();
      onClose();
    } catch (e) {
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setError('Senha incorreta. Tente novamente.');
      } else {
        setError('Erro ao verificar senha.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-handle"></div>
        <div className="modal-title" style={{ color: '#dc2626' }}>
          <span><i className="fas fa-triangle-exclamation" style={{ marginRight: 8 }}></i>Confirmar limpeza</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Warning box */}
        <div style={{ background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 13, marginBottom: 6 }}>
            <i className="fas fa-circle-exclamation" style={{ marginRight: 6 }}></i>Esta ação é irreversível
          </div>
          <ul style={{ fontSize: 12, color: '#7f1d1d', paddingLeft: 16, lineHeight: 1.8, margin: 0 }}>
            <li>Todas as transações serão apagadas</li>
            <li>Todas as contas e cartões serão removidos</li>
            <li>Metas, orçamentos e investimentos serão perdidos</li>
            <li>Os dados da nuvem também serão excluídos</li>
            <li>As categorias padrão serão restauradas</li>
          </ul>
        </div>

        {/* License safe notice */}
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="fas fa-shield-halved" style={{ color: '#1d9e75', fontSize: 14, flexShrink: 0 }}></i>
          <span style={{ fontSize: 12, color: '#14532d' }}>
            <strong>Sua licença não será removida.</strong> Você continuará com acesso ao sistema normalmente após a limpeza.
          </span>
        </div>

        <div className="form-group">
          <label className="form-label">Digite sua senha para autorizar</label>
          <div style={{ position: 'relative' }}>
            <input
              className="form-control"
              type={showPw ? 'text' : 'password'}
              placeholder="••••••"
              value={password}
              autoFocus
              onChange={e => { setPassword(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleConfirm()}
              style={{ paddingRight: 42, borderColor: error ? '#e24b4a' : undefined }}
            />
            <button onClick={() => setShowPw(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14 }}>
              <i className={`fas fa-eye${showPw ? '-slash' : ''}`}></i>
            </button>
          </div>
          {error && (
            <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <i className="fas fa-circle-exclamation"></i> {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1, justifyContent: 'center', padding: 10 }}>
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{ flex: 1, padding: 10, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: loading ? 0.7 : 1, transition: 'background 0.15s' }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#b91c1c'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#dc2626'; }}
          >
            {loading ? <><i className="fas fa-circle-notch fa-spin"></i> Limpando...</> : <><i className="fas fa-trash"></i> Limpar todos os dados</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function LicenseModal({ onClose, onActivated }) {
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = () => {
    setError('');
    const plan = validateKey(key);
    if (!plan) { setError('Chave inválida. Verifique e tente novamente.'); return; }
    setLoading(true);
    setTimeout(() => {
      saveLicense(key.trim().toUpperCase(), plan);
      onActivated();
      onClose();
      toast.success('Licença ativada com sucesso!');
    }, 700);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">
          <span><i className="fas fa-key" style={{ marginRight: 8, color: '#c9a227' }}></i>Reativar Licença</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 20, marginTop: 0 }}>
          Digite sua chave de licença para reativar o acesso ao sistema.
        </p>
        <div className="form-group" style={{ marginBottom: 20 }}>
          <label className="form-label">Chave de licença</label>
          <input
            className="form-control"
            value={key}
            onChange={e => { setKey(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handle()}
            placeholder="MAW-X-XXXXXX-XXXXXXXX"
            autoFocus
            style={{ fontFamily: 'monospace', letterSpacing: 1, borderColor: error ? '#e24b4a' : undefined }}
          />
          {error && <div style={{ color: '#e24b4a', fontSize: 12, marginTop: 6 }}><i className="fas fa-circle-exclamation" style={{ marginRight: 4 }}></i>{error}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>Cancelar</button>
          <button className="btn btn-primary" onClick={handle} disabled={loading || !key.trim()} style={{ flex: 2, justifyContent: 'center' }}>
            {loading ? <><i className="fas fa-circle-notch fa-spin"></i> Verificando...</> : <><i className="fas fa-unlock"></i> Ativar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

const loadPrefs = () => { try { return JSON.parse(localStorage.getItem('maw_prefs') || 'null') || {}; } catch { return {}; } };
const savePrefs = (prefs) => localStorage.setItem('maw_prefs', JSON.stringify(prefs));

function BackupSection() {
  const { state, dispatch } = useApp();
  const [restoredSlot, setRestoredSlot] = useState(null);

  const info = getBackupInfo();
  const daily   = info.daily;
  const preSync = info.preSync;

  const restore = (slot, label) => {
    if (!window.confirm(`Restaurar ${label}? Seus dados atuais serão substituídos.`)) return;
    try {
      const data = JSON.parse(slot);
      dispatch({ type: 'LOAD_CLOUD_DATA', payload: data });
      setRestoredSlot(label);
      toast.success(`${label} restaurado com sucesso!`);
    } catch {
      toast.error('Não foi possível restaurar o backup.');
    }
  };

  const exportSlot = (slot, label) => {
    try {
      const blob = new Blob([slot], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `maw-${label.replace(/\s/g, '-').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exportado com sucesso!');
    } catch {
      toast.error('Erro ao exportar.');
    }
  };

  const exportCurrentData = () => {
    try {
      const KEYS = ['accounts','cards','categories','transactions','transfers','goals','budgets','investments','debts'];
      const payload = {};
      KEYS.forEach(k => { if (state[k] !== undefined) payload[k] = state[k]; });
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `maw-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Dados exportados com sucesso!');
    } catch {
      toast.error('Erro ao exportar dados.');
    }
  };

  const formatTs = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString('pt-BR'); } catch { return ts; }
  };

  // ── Limpeza de teste: Consórcio / Capitalização ──────────────────────────────
  // Identifica os investimentos desses tipos e os lançamentos ligados a eles:
  // por investmentId, por descrição terminando em "— {nome do investimento}"
  // (padrão do extrato do ativo) ou por menção no texto/subcategoria.
  const [showCleanupList, setShowCleanupList] = useState(false);
  const testInvestments = (state.investments || []).filter(i => i.type === 'Consórcio' || i.type === 'Capitalização');
  const testInvIds = new Set(testInvestments.map(i => i.id));
  const testSuffixes = testInvestments.map(i => `— ${i.name}`);
  const matchTestTx = (t) => {
    if (t.investmentId && testInvIds.has(t.investmentId)) return true;
    const desc = t.description || '';
    if (testSuffixes.some(s => desc.endsWith(s))) return true;
    const hay = `${desc} ${t.subcategory || ''}`.toLowerCase();
    return hay.includes('consórcio') || hay.includes('consorcio') || hay.includes('capitaliz');
  };
  const testTxs = (state.transactions || []).filter(matchTestTx);
  const fmtMoneyBR = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Conserto de faturas: compras de cartão lançadas pelo WhatsApp/Telegram ──
  // Até a v1.11.2 o bot gravava a compra na DATA DA COMPRA, e não no vencimento
  // da fatura (que é como o app agrupa cartão). Resultado: a compra caía na
  // fatura errada e o total não batia com o cartão de verdade. Estes são os
  // lançamentos antigos afetados — os novos já nascem certos.
  const [showCardFixList, setShowCardFixList] = useState(false);
  const [cardFixOff, setCardFixOff] = useState(() => new Set());
  const cardsById = Object.fromEntries((state.cards || []).map(c => [c.id, c]));
  const cardFixItems = (state.transactions || [])
    .filter(t => t.cardId && t.type === 'expense' && !t.purchaseDate && /^(whatsapp|telegram)/.test(t.createdVia || ''))
    .map(t => {
      const card = cardsById[t.cardId];
      return card ? { tx: t, card, newDate: calcCardDueDate(card, t.date) } : null;
    })
    .filter(x => x && x.newDate !== x.tx.date)
    .sort((a, b) => (a.tx.date < b.tx.date ? 1 : -1));
  const cardFixSelected = cardFixItems.filter(i => !cardFixOff.has(i.tx.id));

  const toggleCardFix = (id) => setCardFixOff(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const runCardFix = () => {
    if (!cardFixSelected.length) { toast.info('Nenhum lançamento selecionado.'); return; }
    if (!window.confirm(`Mover ${cardFixSelected.length} lançamento(s) para a fatura correta?\n\nO valor não muda — só a data, que passa a ser o vencimento da fatura. A data da compra fica guardada.`)) return;
    cardFixSelected.forEach(({ tx, newDate }) => {
      dispatch({ type: 'UPDATE_TRANSACTION', payload: { ...tx, purchaseDate: tx.date, date: newDate } });
    });
    toast.success(`${cardFixSelected.length} lançamento(s) movido(s) para a fatura certa.`);
  };

  const runTestCleanup = (alsoRemoveInvestments) => {
    const n = testTxs.length, m = testInvestments.length;
    if (n === 0 && m === 0) { toast.info('Nada de Consórcio/Capitalização pra limpar.'); return; }
    const msg = alsoRemoveInvestments
      ? `Apagar ${n} lançamento(s) E remover ${m} investimento(s) de Consórcio/Capitalização?\n\nHá backup, mas não dá pra desfazer com um clique.`
      : `Apagar ${n} lançamento(s) e zerar ${m} investimento(s) de Consórcio/Capitalização (os investimentos continuam cadastrados)?\n\nHá backup, mas não dá pra desfazer com um clique.`;
    if (!window.confirm(msg)) return;
    testTxs.forEach(t => dispatch({ type: 'DELETE_TRANSACTION', payload: t.id }));
    testInvestments.forEach(inv => {
      if (alsoRemoveInvestments) {
        dispatch({ type: 'DELETE_INVESTMENT', payload: inv.id });
      } else {
        dispatch({ type: 'UPDATE_INVESTMENT', payload: { ...inv, invested: 0, current: 0, dividendsReceived: 0 } });
      }
    });
    toast.success(`Limpeza feita: ${n} lançamento(s) removido(s)${alsoRemoveInvestments ? ` e ${m} investimento(s) apagado(s)` : ` e ${m} investimento(s) zerado(s)`}.`);
  };

  return (
    <div style={{ maxWidth: 860, marginTop: 8 }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 24px', background: 'var(--card)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-clock-rotate-left" style={{ color: '#378ADD' }}></i> Backup de Dados
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
          Mantenha seus dados seguros exportando regularmente ou usando os backups automáticos abaixo.
        </div>

        {/* Export current data — always visible */}
        <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Exportar dados atuais</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
            Baixa um arquivo JSON com todos os seus dados financeiros agora.
          </div>
          <button
            onClick={exportCurrentData}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: '#378ADD', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
          >
            <i className="fas fa-download"></i> Exportar agora
          </button>
        </div>

        {/* Slot A: daily backup from cloud */}
        {daily.data && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  <i className="fas fa-cloud-arrow-down" style={{ color: '#1d9e75', marginRight: 6 }}></i>
                  Backup diário da nuvem
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Salvo em {daily.date}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => exportSlot(daily.data, `backup-diario-${daily.date}`)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                >
                  <i className="fas fa-download"></i> Exportar
                </button>
                <button
                  onClick={() => restore(daily.data, 'backup diário')}
                  disabled={restoredSlot === 'backup diário'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: restoredSlot === 'backup diário' ? 'var(--surface2)' : '#1d9e75', color: restoredSlot === 'backup diário' ? 'var(--muted)' : '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 12, cursor: restoredSlot === 'backup diário' ? 'default' : 'pointer' }}
                >
                  <i className={`fas fa-${restoredSlot === 'backup diário' ? 'check' : 'rotate-left'}`}></i>
                  {restoredSlot === 'backup diário' ? 'Restaurado!' : 'Restaurar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Slot B: pre-sync backup */}
        {preSync.data && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  <i className="fas fa-shield-halved" style={{ color: '#c9a227', marginRight: 6 }}></i>
                  Backup pré-sincronização
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  Estado local salvo em {formatTs(preSync.ts)} — antes da última sincronização
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => exportSlot(preSync.data, `pre-sync-${preSync.ts?.slice(0,10)}`)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                >
                  <i className="fas fa-download"></i> Exportar
                </button>
                <button
                  onClick={() => restore(preSync.data, 'backup pré-sincronização')}
                  disabled={restoredSlot === 'backup pré-sincronização'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: restoredSlot === 'backup pré-sincronização' ? 'var(--surface2)' : '#c9a227', color: restoredSlot === 'backup pré-sincronização' ? 'var(--muted)' : '#0d1b2a', border: 'none', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 12, cursor: restoredSlot === 'backup pré-sincronização' ? 'default' : 'pointer' }}
                >
                  <i className={`fas fa-${restoredSlot === 'backup pré-sincronização' ? 'check' : 'rotate-left'}`}></i>
                  {restoredSlot === 'backup pré-sincronização' ? 'Restaurado!' : 'Restaurar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!daily.data && !preSync.data && (
          <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>
            Nenhum backup automático disponível ainda. Faça login novamente para gerar o primeiro backup.
          </div>
        )}
      </div>

      {/* Conserto de faturas — compras de cartão vindas do WhatsApp/Telegram */}
      {cardFixItems.length > 0 && (
        <div style={{ border: '1px solid rgba(55,138,221,0.35)', borderRadius: 'var(--radius)', padding: '20px 24px', background: 'var(--card)', marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-file-invoice-dollar" style={{ color: '#378ADD' }}></i> Corrigir faturas de cartão
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
            Encontrei <strong style={{ color: 'var(--text)' }}>{cardFixItems.length}</strong> compra(s) de cartão lançada(s) pelo WhatsApp/Telegram na data da compra, e não na fatura em que elas caem —
            por isso a fatura do mês não batia com a do cartão. Posso mover cada uma para o vencimento da fatura correta (o valor não muda).
            Se você já ajustou alguma na mão, é só desmarcar.
          </div>

          <button onClick={() => setShowCardFixList(v => !v)} className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
            <i className={`fas fa-chevron-${showCardFixList ? 'up' : 'down'}`} style={{ marginRight: 6 }}></i>
            {showCardFixList ? 'Esconder' : 'Ver'} os {cardFixItems.length} lançamento(s)
          </button>

          {showCardFixList && (
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
              {cardFixItems.map(({ tx, card, newDate }) => (
                <label key={tx.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!cardFixOff.has(tx.id)} onChange={() => toggleCardFix(tx.id)} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tx.description || '(sem descrição)'} <span style={{ color: 'var(--muted)' }}>· {card.name}</span>
                  </span>
                  <span style={{ color: 'var(--muted)', flexShrink: 0 }}>
                    R$ {fmtMoneyBR(tx.value)} · {fmtDateBR(tx.date)} <i className="fas fa-arrow-right" style={{ fontSize: 9, margin: '0 3px' }}></i>
                    <strong style={{ color: '#378ADD' }}>{fmtDateBR(newDate)}</strong>
                  </span>
                </label>
              ))}
            </div>
          )}

          <button onClick={runCardFix} className="btn btn-primary btn-sm" disabled={!cardFixSelected.length}>
            <i className="fas fa-wand-magic-sparkles"></i> Mover {cardFixSelected.length} lançamento(s) para a fatura certa
          </button>
        </div>
      )}

      {/* Limpeza de teste — Consórcio / Capitalização */}
      {(testInvestments.length > 0 || testTxs.length > 0) && (
        <div style={{ border: '1px solid rgba(226,75,74,0.35)', borderRadius: 'var(--radius)', padding: '20px 24px', background: 'var(--card)', marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-broom" style={{ color: '#e24b4a' }}></i> Limpar lançamentos de Consórcio/Capitalização
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
            Encontrei <strong style={{ color: 'var(--text)' }}>{testTxs.length}</strong> lançamento(s) e <strong style={{ color: 'var(--text)' }}>{testInvestments.length}</strong> investimento(s) de Consórcio/Capitalização.
            Confira a lista antes de apagar — a ação passa pelo backup, mas não dá pra desfazer com um clique.
          </div>

          <button onClick={() => setShowCleanupList(v => !v)} className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
            <i className={`fas fa-chevron-${showCleanupList ? 'up' : 'down'}`} style={{ marginRight: 6 }}></i>
            {showCleanupList ? 'Esconder' : 'Ver'} os {testTxs.length} lançamento(s)
          </button>

          {showCleanupList && (
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
              {testTxs.length === 0 ? (
                <div style={{ padding: '14px', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>Nenhum lançamento — só os investimentos serão zerados/removidos.</div>
              ) : testTxs.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description || '(sem descrição)'}</span>
                  <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{t.date?.split('-').reverse().join('/')} · R$ {fmtMoneyBR(t.value)}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => runTestCleanup(false)} className="btn btn-primary btn-sm">
              <i className="fas fa-eraser"></i> Apagar lançamentos e zerar os investimentos
            </button>
            <button onClick={() => runTestCleanup(true)} className="btn btn-ghost btn-sm" style={{ color: '#e24b4a', borderColor: 'rgba(226,75,74,0.4)' }}>
              <i className="fas fa-trash"></i> Apagar lançamentos e os investimentos também
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Número do bot WhatsApp — (19) 97125-7017, número real de produção
const WA_BOT_NUMBER_DISPLAY = '(19) 97125-7017';

function WhatsAppSection() {
  const { state, dispatch } = useApp();
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);

  const trial = getTrialStatus(state.user, state.license);
  const allowed = trial.active || hasFeature(state.license?.plan, 'whatsapp');

  const genCode = async () => {
    if (!auth.currentUser) { toast.error('Faça login para vincular o WhatsApp.'); return; }
    setLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const r = await fetch('https://southamerica-east1-maw-financas.cloudfunctions.net/waLinkCode', {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const d = await r.json();
      if (d.code) setCode(d.code);
      else toast.error('Não foi possível gerar o código. Tente novamente.');
    } catch {
      toast.error('Erro de conexão ao gerar o código.');
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 860, marginTop: 8 }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 24px', background: 'var(--card-bg)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fab fa-whatsapp" style={{ color: '#25D366', fontSize: 17 }}></i> WhatsApp com IA
          <span style={{ fontSize: 9, background: 'var(--gold)', color: '#0d1b2a', borderRadius: 99, padding: '2px 8px', fontWeight: 700 }}>COMPLETO</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
          Mande a foto de um comprovante no WhatsApp e a IA analisa e pergunta se quer lançar — valor, estabelecimento e categoria, sem digitar nada.
        </div>

        {!allowed ? (
          <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => dispatch({ type: 'SET_TAB', payload: 'plans' })}>
            <i className="fas fa-crown"></i> Disponível no plano Completo
          </button>
        ) : code ? (
          <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Seu código de vínculo (válido por 15 minutos):</div>
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 8, color: 'var(--gold)', marginBottom: 12, fontFamily: 'monospace' }}>{code}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.7 }}>
              1. Abra o WhatsApp no seu celular<br />
              2. Envie este código para <strong>{WA_BOT_NUMBER_DISPLAY}</strong><br />
              3. Pronto! Depois é só mandar fotos de comprovantes 📸
            </div>
            <button className="btn btn-ghost" style={{ fontSize: 12, marginTop: 12 }} onClick={genCode} disabled={loading}>
              <i className="fas fa-rotate"></i> Gerar novo código
            </button>
          </div>
        ) : (
          <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={genCode} disabled={loading}>
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Gerando...</> : <><i className="fab fa-whatsapp"></i> Vincular meu WhatsApp</>}
          </button>
        )}
      </div>
    </div>
  );
}

function FamilySection() {
  const { state, dispatch } = useApp();
  const [joinCode, setJoinCode] = useState('');
  const [copied, setCopied] = useState(false);
  const myCode = auth.currentUser?.uid || '';
  const inFamily = !!state.familyId;
  const isOwner = state.familyId === myCode;

  // Família é recurso do plano Completo (e legados Pro/Família) — mas quem
  // já está numa família nunca é travado (não estrangular usuários existentes)
  const trial = getTrialStatus(state.user, state.license);
  const familyAllowed = inFamily || trial.active || hasFeature(state.license?.plan, 'family');

  if (!familyAllowed) {
    return (
      <div style={{ maxWidth: 860, marginTop: 8 }}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 24px', background: 'var(--card-bg)' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-users" style={{ color: '#378ADD' }}></i> Multi-usuário Familiar
            <span style={{ fontSize: 9, background: 'var(--gold)', color: '#0d1b2a', borderRadius: 99, padding: '2px 8px', fontWeight: 700 }}>COMPLETO</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
            Compartilhe as finanças com a família em tempo real. Disponível no plano Completo.
          </div>
          <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => dispatch({ type: 'SET_TAB', payload: 'plans' })}>
            <i className="fas fa-crown"></i> Ver planos
          </button>
        </div>
      </div>
    );
  }

  const copyCode = () => {
    navigator.clipboard.writeText(myCode).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const createFamily = () => {
    dispatch({ type: 'SET_FAMILY', payload: myCode });
    toast.success('Família criada! Compartilhe seu código com os familiares.');
  };

  const joinFamily = () => {
    if (!joinCode.trim()) { toast.error('Digite o código familiar.'); return; }
    dispatch({ type: 'SET_FAMILY', payload: joinCode.trim() });
    toast.success('Você entrou na família! Os dados serão sincronizados na próxima vez que entrar.');
  };

  const leaveFamily = () => {
    if (!window.confirm('Sair da família? Você voltará a usar sua conta individual.')) return;
    dispatch({ type: 'SET_FAMILY', payload: null });
    toast.success('Você saiu da família.');
  };

  return (
    <div style={{ maxWidth: 860, marginTop: 8 }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 24px', background: 'var(--card)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-users" style={{ color: '#378ADD' }}></i> Multi-usuário Familiar
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
          Compartilhe seus dados financeiros com a família. Todos os membros sincronizam em tempo real.
        </div>

        {!inFamily ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Create family */}
            <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Criar uma família</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
                Gera um código único que seus familiares podem usar para se conectar.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, flex: 1, wordBreak: 'break-all', color: 'var(--muted)' }}>{myCode}</span>
                <button onClick={copyCode} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#378ADD', fontSize: 13 }} title="Copiar">
                  <i className={`fas fa-${copied ? 'check' : 'copy'}`}></i>
                </button>
              </div>
              <button className="btn btn-primary" onClick={createFamily} style={{ fontSize: 13 }}>
                <i className="fas fa-plus"></i> Criar família com meu código
              </button>
            </div>

            {/* Join family */}
            <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Entrar em uma família</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
                Digite o código fornecido pelo responsável da família.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-control"
                  placeholder="Cole o código familiar aqui..."
                  value={joinCode}
                  onChange={e => setJoinCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && joinFamily()}
                  style={{ flex: 1, fontSize: 12, padding: '8px 12px' }}
                />
                <button className="btn btn-primary" onClick={joinFamily} style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  <i className="fas fa-right-to-bracket"></i> Entrar
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#1d9e7520', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-check-circle" style={{ color: '#1d9e75', fontSize: 16 }}></i>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1d9e75' }}>{isOwner ? 'Você é o responsável da família' : 'Conectado à família'}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Dados sincronizados com a família</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
              Código: <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{state.familyId?.slice(0, 16)}…</span>
            </div>
            {isOwner && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, background: '#fff', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 11, flex: 1, wordBreak: 'break-all', color: 'var(--muted)' }}>{myCode}</span>
                <button onClick={copyCode} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#378ADD', fontSize: 13 }}>
                  <i className={`fas fa-${copied ? 'check' : 'copy'}`}></i>
                </button>
              </div>
            )}
            <button onClick={leaveFamily} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 14px', fontSize: 12, color: '#e24b4a', cursor: 'pointer', fontWeight: 600 }}>
              <i className="fas fa-right-from-bracket" style={{ marginRight: 5 }}></i>Sair da família
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfilePage() {
  const { state, dispatch } = useApp();
  const [nameForm, setNameForm] = useState({ name: state.user?.name || '' });
  const [passForm, setPassForm] = useState({ current: '', newPass: '', confirm: '' });
  const [showPass, setShowPass] = useState({ current: false, new: false, confirm: false });
  const [nameLoading, setNameLoading] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [pushStatus, setPushStatus] = useState(() => 'Notification' in window ? Notification.permission : 'unsupported');
  const [pushLoading, setPushLoading] = useState(false);
  const fileRef = useRef();

  const updatePref = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    savePrefs(next);
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Selecione uma imagem válida.'); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('Imagem muito grande. Máximo 3MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const updated = { ...state.user, avatar: ev.target.result };
      dispatch({ type: 'SET_USER', payload: updated });
      const profile = { name: updated.name, email: updated.email, initials: updated.initials, avatar: updated.avatar };
      localStorage.setItem('maw_profile', JSON.stringify(profile));
      toast.success('Foto de perfil atualizada!');
    };
    reader.readAsDataURL(file);
  };

  const initials = nameForm.name.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  const saveName = async () => {
    if (!nameForm.name.trim()) { toast.error('Digite um nome válido.'); return; }
    setNameLoading(true);
    try {
      const trimmed = nameForm.name.trim();
      // Salva no Firebase Auth para persistir em qualquer sessão/dispositivo
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: trimmed });
      }
      const updated = { ...state.user, name: trimmed, initials };
      dispatch({ type: 'SET_USER', payload: updated });
      const profile = { name: updated.name, email: updated.email, initials: updated.initials, avatar: updated.avatar };
      localStorage.setItem('maw_profile', JSON.stringify(profile));
      toast.success('Nome atualizado com sucesso!');
    } catch {
      toast.error('Erro ao atualizar nome. Tente novamente.');
    } finally {
      setNameLoading(false);
    }
  };

  const savePassword = async () => {
    if (!passForm.current) { toast.error('Digite a senha atual.'); return; }
    if (passForm.newPass.length < 6) { toast.error('Nova senha deve ter ao menos 6 caracteres.'); return; }
    if (passForm.newPass !== passForm.confirm) { toast.error('As senhas não coincidem.'); return; }
    setPassLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, passForm.current);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passForm.newPass);
      toast.success('Senha alterada com sucesso!');
      setPassForm({ current: '', newPass: '', confirm: '' });
    } catch (e) {
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        toast.error('Senha atual incorreta.');
      } else {
        toast.error('Erro ao alterar senha. Tente novamente.');
      }
    } finally {
      setPassLoading(false);
    }
  };

  const handleClearData = async () => {
    dispatch({ type: 'CLEAR_ALL_DATA' });
    const uid = auth.currentUser?.uid;
    const familyId = state.familyId;
    if (uid) {
      try {
        await deleteCloudData(uid, familyId);
        toast.success('Todos os dados foram limpos localmente e na nuvem.');
      } catch {
        toast.success('Dados locais limpos. Tente novamente para remover da nuvem.');
      }
    } else {
      toast.success('Todos os dados foram limpos com sucesso.');
    }
  };

  const handlePushToggle = async (e) => {
    if (pushLoading) return;
    const enable = e.target.checked;
    if (!enable) {
      setPushLoading(true);
      await disablePushNotifications(auth.currentUser?.uid);
      updatePref('pushEnabled', false);
      setPushLoading(false);
      toast.success('Notificações push desativadas.');
      return;
    }
    setPushLoading(true);
    const result = await requestPushPermission(auth.currentUser?.uid);
    if (result.success) {
      updatePref('pushEnabled', true);
      setPushStatus('granted');
      toast.success('Notificações push ativadas! Você receberá alertas no celular.');
    } else if (result.reason === 'denied') {
      setPushStatus('denied');
      updatePref('pushEnabled', false);
      toast.error('Permissão negada. Habilite nas configurações do navegador/SO.');
    } else {
      toast.error('Erro: ' + (result.error || result.reason || 'desconhecido'));
    }
    setPushLoading(false);
  };

  const SETTINGS_TABS = [
    { id: 'perfil',       label: 'Perfil',       icon: 'fa-user' },
    { id: 'preferencias', label: 'Preferências', icon: 'fa-sliders' },
    { id: 'licenca',      label: 'Licença',      icon: 'fa-key' },
    { id: 'whatsapp',     label: 'WhatsApp',     icon: 'fa-whatsapp', brand: true },
    { id: 'dados',        label: 'Dados',        icon: 'fa-database' },
  ];
  const [activeSection, setActiveSection] = useState('perfil');

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Configurações</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Gerencie seu perfil e preferências</div>
      </div>

      {/* Abas */}
      <div className="settings-tabs">
        {SETTINGS_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveSection(t.id)}
            className={`settings-tab ${activeSection === t.id ? 'active' : ''}`}
          >
            <i className={`${t.brand ? 'fab' : 'fas'} ${t.icon}`}></i> {t.label}
          </button>
        ))}
      </div>

      {activeSection === 'perfil' && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 860 }}>

        {/* Card avatar + info */}
        <div className="card" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 20, padding: '20px 24px' }}>
          {/* Clickable avatar */}
          <div
            style={{ position: 'relative', width: 70, height: 70, flexShrink: 0, cursor: 'pointer' }}
            onClick={() => fileRef.current?.click()}
            onMouseEnter={() => setAvatarHover(true)}
            onMouseLeave={() => setAvatarHover(false)}
          >
            {state.user?.avatar ? (
              <img src={state.user.avatar} alt="avatar" style={{ width: 70, height: 70, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'linear-gradient(135deg,var(--navy),var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 26, fontWeight: 800 }}>
                {initials}
              </div>
            )}
            {/* Camera overlay */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: 'rgba(0,0,0,0.48)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: avatarHover ? 1 : 0, transition: 'opacity 0.15s',
            }}>
              <i className="fas fa-camera" style={{ color: '#fff', fontSize: 20 }}></i>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 18 }}>{state.user?.name}</div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2 }}>{state.user?.email}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              <i className="fas fa-camera" style={{ marginRight: 4 }}></i>Clique na foto para alterar
            </div>
          </div>
          <button className="btn btn-danger btn-sm" onClick={() => signOut(auth)}>
            <i className="fas fa-sign-out-alt"></i> Sair da conta
          </button>
        </div>

        {/* Editar nome */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#e6f1fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-user-pen" style={{ color: 'var(--blue)', fontSize: 15 }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Editar nome</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Altere como seu nome é exibido</div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Nome completo</label>
            <input
              className="form-control"
              type="text"
              value={nameForm.name}
              onChange={e => setNameForm({ name: e.target.value })}
              placeholder="Seu nome completo"
            />
          </div>
          <button className="btn btn-primary btn-sm" onClick={saveName} disabled={nameLoading} style={{ width: '100%', justifyContent: 'center', padding: '10px' }}>
            {nameLoading ? <><i className="fas fa-circle-notch fa-spin"></i> Salvando...</> : <><i className="fas fa-check"></i> Salvar nome</>}
          </button>
        </div>

        {/* Trocar senha */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#faeeda', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-lock" style={{ color: '#854F0B', fontSize: 15 }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Trocar senha</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Atualize sua senha de acesso</div>
            </div>
          </div>
          {[
            { key: 'current', label: 'Senha atual', show: showPass.current, toggle: () => setShowPass(s => ({...s, current: !s.current})) },
            { key: 'newPass', label: 'Nova senha', show: showPass.new, toggle: () => setShowPass(s => ({...s, new: !s.new})) },
            { key: 'confirm', label: 'Confirmar nova senha', show: showPass.confirm, toggle: () => setShowPass(s => ({...s, confirm: !s.confirm})) },
          ].map(f => (
            <div className="form-group" key={f.key}>
              <label className="form-label">{f.label}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control"
                  type={f.show ? 'text' : 'password'}
                  placeholder="••••••"
                  value={passForm[f.key]}
                  onChange={e => setPassForm(p => ({...p, [f.key]: e.target.value}))}
                  style={{ paddingRight: 40 }}
                />
                <button onClick={f.toggle} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 13 }}>
                  <i className={`fas fa-eye${f.show ? '-slash' : ''}`}></i>
                </button>
              </div>
            </div>
          ))}
          <button className="btn btn-primary btn-sm" onClick={savePassword} disabled={passLoading} style={{ width: '100%', justifyContent: 'center', padding: '10px' }}>
            {passLoading ? <><i className="fas fa-circle-notch fa-spin"></i> Alterando...</> : <><i className="fas fa-key"></i> Alterar senha</>}
          </button>
        </div>

      </div>
      )}

      {activeSection === 'preferencias' && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, maxWidth: 860 }}>

        {/* Tema de cores */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-palette" style={{ color: '#9333ea', fontSize: 15 }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Tema de cores</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Personalize a aparência do sistema</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              { id: 'default',   label: 'Padrão',    color: '#0d1b2a', accent: '#c9a227' },
              { id: 'esmeralda', label: 'Esmeralda', color: '#064e3b', accent: '#34d399' },
              { id: 'ocean',     label: 'Ocean',      color: '#0c2461', accent: '#4fc3f7' },
              { id: 'roxo',      label: 'Roxo',       color: '#3b0764', accent: '#c084fc' },
              { id: 'rose',      label: 'Rose',       color: '#881337', accent: '#fb7185' },
            ].map(t => (
              <div key={t.id} onClick={() => dispatch({ type: 'SET_THEME', payload: t.id })} style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg,${t.color},${t.accent})`, marginBottom: 6, border: state.theme === t.id ? '3px solid var(--gold)' : '3px solid transparent', boxShadow: state.theme === t.id ? '0 0 0 2px var(--gold)' : 'none', transition: 'all 0.15s' }} />
                <div style={{ fontSize: 11, color: state.theme === t.id ? 'var(--text)' : 'var(--muted)', fontWeight: state.theme === t.id ? 700 : 400 }}>{t.label}</div>
              </div>
            ))}
          </div>
        </div>

      {/* Preferências de Alertas */}
      <div className="card" style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-bell" style={{ color: '#f59e0b', fontSize: 15 }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Notificações de Vencimento</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Configure alertas para contas próximas do vencimento</div>
            </div>
          </div>

          {/* Toggle alertas ativos */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-envelope" style={{ color: 'var(--muted)', fontSize: 13 }}></i>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Notificações no sistema</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Receba alertas dentro do MAW Finanças</div>
              </div>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0, cursor: 'pointer' }}>
              <input type="checkbox" checked={prefs.alertsEnabled !== false} onChange={e => updatePref('alertsEnabled', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
              <span style={{
                position: 'absolute', inset: 0, borderRadius: 99, cursor: 'pointer',
                background: prefs.alertsEnabled !== false ? 'var(--blue)' : 'var(--surface2)',
                border: '1px solid var(--border)', transition: 'background 0.2s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: prefs.alertsEnabled !== false ? 22 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s',
                }} />
              </span>
            </label>
          </div>

          {/* Notificações Push */}
          {isPushSupported() && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: prefs.pushEnabled && pushStatus === 'granted' ? '#d1fae5' : 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-mobile-screen" style={{ color: prefs.pushEnabled && pushStatus === 'granted' ? '#1d9e75' : 'var(--muted)', fontSize: 13 }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Notificações Push (celular)</div>
                  <div style={{ fontSize: 11, color: pushStatus === 'denied' ? '#e24b4a' : 'var(--muted)' }}>
                    {pushStatus === 'denied'
                      ? 'Bloqueado — permita nas configurações do navegador'
                      : prefs.pushEnabled && pushStatus === 'granted'
                      ? 'Ativas — você receberá alertas no celular'
                      : 'Receba alertas mesmo com o app fechado'}
                  </div>
                </div>
              </div>
              {pushStatus === 'denied' ? (
                <span style={{ fontSize: 11, color: '#e24b4a', fontWeight: 700, flexShrink: 0 }}>Bloqueado</span>
              ) : (
                <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0, cursor: pushLoading ? 'wait' : 'pointer', opacity: pushLoading ? 0.6 : 1 }}>
                  <input
                    type="checkbox"
                    checked={!!prefs.pushEnabled && pushStatus === 'granted'}
                    onChange={handlePushToggle}
                    disabled={pushLoading}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', inset: 0, borderRadius: 99,
                    background: prefs.pushEnabled && pushStatus === 'granted' ? '#1d9e75' : 'var(--surface2)',
                    border: '1px solid var(--border)', transition: 'background 0.2s',
                  }}>
                    <span style={{
                      position: 'absolute', top: 2,
                      left: prefs.pushEnabled && pushStatus === 'granted' ? 22 : 2,
                      width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s',
                    }} />
                  </span>
                </label>
              )}
            </div>
          )}

          {/* Dias antes do vencimento */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border)', opacity: prefs.alertsEnabled !== false ? 1 : 0.4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-calendar-days" style={{ color: 'var(--muted)', fontSize: 13 }}></i>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Dias antes do vencimento</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Você será notificado {prefs.alertDays ?? 3} dia{(prefs.alertDays ?? 3) !== 1 ? 's' : ''} antes do vencimento</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number" min={1} max={30}
                value={prefs.alertDays ?? 3}
                onChange={e => updatePref('alertDays', Math.max(1, Math.min(30, parseInt(e.target.value) || 3)))}
                disabled={prefs.alertsEnabled === false}
                style={{ width: 56, textAlign: 'center', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontWeight: 600 }}
              />
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>dias</span>
            </div>
          </div>

          {/* Modo escuro */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className={`fas ${state.darkMode ? 'fa-sun' : 'fa-moon'}`} style={{ color: 'var(--muted)', fontSize: 13 }}></i>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Modo Escuro</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Alterne entre tema claro e escuro</div>
              </div>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!state.darkMode} onChange={() => dispatch({ type: 'TOGGLE_DARK' })} style={{ opacity: 0, width: 0, height: 0 }} />
              <span style={{
                position: 'absolute', inset: 0, borderRadius: 99, cursor: 'pointer',
                background: state.darkMode ? 'var(--blue)' : 'var(--surface2)',
                border: '1px solid var(--border)', transition: 'background 0.2s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: state.darkMode ? 22 : 2,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s',
                }} />
              </span>
            </label>
          </div>
        </div>

      </div>
      )}

      {activeSection === 'licenca' && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, maxWidth: 860 }}>

        {/* Minha Licença */}
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '20px 24px',
          background: 'var(--card)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-key" style={{ color: '#c9a227' }}></i> Minha Licença
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                {state.license ? (
                  <>
                    Plano: <strong style={{ color: 'var(--text)' }}>{PLAN_LABEL[state.license.plan] || state.license.plan}</strong>
                    <span style={{ margin: '0 10px', opacity: 0.3 }}>|</span>
                    Chave: <span style={{ fontFamily: 'monospace', letterSpacing: 1, color: 'var(--text)' }}>
                      {state.license.key.slice(0, 8)}••••••••••••
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#e24b4a' }}>Nenhuma licença ativa</span>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowLicenseModal(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '9px 18px',
                background: 'var(--bg)', color: '#c9a227',
                border: '1.5px solid #c9a227',
                borderRadius: 'var(--radius-sm)',
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#c9a22720'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg)'; }}
            >
              <i className="fas fa-rotate"></i> Reativar licença
            </button>
          </div>
        </div>

        {/* Multi-usuário Familiar */}
        {!IS_DEMO && <FamilySection />}
      </div>
      )}

      {activeSection === 'whatsapp' && !IS_DEMO && (
        <div style={{ maxWidth: 860 }}>
          <WhatsAppSection />
        </div>
      )}

      {activeSection === 'dados' && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, maxWidth: 860 }}>

        {/* Backup e Restauração */}
        <BackupSection />

        {/* Zona de Perigo */}
        <div style={{
          border: '1px solid #fca5a5',
          borderRadius: 'var(--radius)',
          padding: '20px 24px',
          background: '#fff5f5',
        }}>
          <div style={{ color: '#dc2626', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
            Zona de Perigo
          </div>
          <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 16, opacity: 0.8 }}>
            Ações irreversíveis. Prossiga com cuidado.
          </div>
          <button
            onClick={() => setShowClearModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '9px 18px',
              background: '#fff', color: '#dc2626',
              border: '1.5px solid #fca5a5',
              borderRadius: 'var(--radius-sm)',
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#dc2626'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#fca5a5'; }}
          >
            <i className="fas fa-trash-can"></i> Limpar todos os dados
          </button>
        </div>
      </div>
      )}

      {showClearModal && (
        <ClearDataModal
          onClose={() => setShowClearModal(false)}
          onConfirm={handleClearData}
        />
      )}

      {showLicenseModal && (
        <LicenseModal
          onClose={() => setShowLicenseModal(false)}
          onActivated={() => { const lic = loadLicense(); if (lic) lic.email = auth.currentUser?.email ?? null; dispatch({ type: 'SET_LICENSE', payload: lic }); }}
        />
      )}
    </div>
  );
}

function CategoriesPage() {
  const { state } = useApp();
  return (
    <div>
      <div className="row" style={{ marginBottom: 20 }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>Categorias</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: 10 }}>
        {state.categories.map(cat => (
          <div key={cat.id} className="card-sm" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={cat.icon} style={{ color: '#4f46e5', fontSize: 15 }}></i>
            </div>
            <span style={{ fontWeight: 500, fontSize: 13 }}>{cat.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(true);
  return (
    <AppProvider>
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      <AppContent />
    </AppProvider>
  );
}
